import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getData, postData } from '../../services/apiService';
import Layout from '../../components/layout/Layout';
import 'bootstrap/dist/css/bootstrap.min.css';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';

const QuizSubmission = () => {
    const [searchParams] = useSearchParams();
    const quizId = searchParams.get('quizId');
    const navigate = useNavigate();
    const [quiz, setQuiz] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submissionResult, setSubmissionResult] = useState(null);
    const [timer, setTimer] = useState(0);
    const [timeUp, setTimeUp] = useState(false);
    const [alreadySubmitted, setAlreadySubmitted] = useState(false); // New state

    // Fetch quiz and questions data
    useEffect(() => {
        const fetchQuizData = async () => {
            try {
                const [quizRes, questionsRes] = await Promise.all([
                    getData(`/get-quiz-by-id/${quizId}`),
                    getData(`/get-all-question-by-quiz/${quizId}`)
                ]);

                setQuiz(quizRes);
                setQuestions(questionsRes);

                // Initialize timer if quiz has time limit
                if (quizRes.timeLimit) {
                    setTimer(quizRes.timeLimit * 60); // Convert minutes to seconds
                }

                setLoading(false);
            } catch (err) {
                // Check if the error is "already submitted"
                if (err.response?.data?.error === "Access denied. You have already submitted this quiz") {
                    setAlreadySubmitted(true);
                } else {
                    setError('Failed to load quiz data');
                }
                setLoading(false);
                console.error(err);
            }
        };

        fetchQuizData();
    }, [quizId]);

    // Timer countdown
    useEffect(() => {
        if (timer <= 0 || !quiz?.timeLimit) return;

        const interval = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    setTimeUp(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [timer, quiz?.timeLimit]);

    // Auto-submit when time is up
    useEffect(() => {
        if (timeUp) {
            handleSubmit();
        }
    }, [timeUp]);

    const handleAnswerChange = (questionId, option, isChecked) => {
        setAnswers(prev => {
            const newAnswers = { ...prev };

            if (!newAnswers[questionId]) {
                newAnswers[questionId] = [];
            }

            if (isChecked) {
                // Add the option if checked
                if (!newAnswers[questionId].includes(option)) {
                    newAnswers[questionId] = [...newAnswers[questionId], option];
                }
            } else {
                // Remove the option if unchecked
                newAnswers[questionId] = newAnswers[questionId].filter(o => o !== option);
            }

            return newAnswers;
        });
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (submitting || alreadySubmitted) return;

        setSubmitting(true);

        try {
            // Format answers for API
            const formattedAnswers = Object.keys(answers).map(questionId => ({
                questionId,
                selectedOptions: answers[questionId]
            }));

            const response = await postData('/submit-quiz-answers', {
                quizId,
                answers: formattedAnswers,
            });

            setSubmissionResult(response);
        } catch (err) {
            setError('Failed to submit quiz');
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    if (loading) {
        return (
            <Layout>
                <div className="d-flex justify-content-center mt-5">
                    <Spinner animation="border" role="status">
                        {/* <span className="visually-hidden">Loading...</span> */}
                    </Spinner>
                </div>
            </Layout>
        );
    }

    if (alreadySubmitted) {
        return (
            <Layout>
                <div className="container mt-5">
                    <Alert variant="warning">
                        <Alert.Heading>Access Denied</Alert.Heading>
                        <p>You have already submitted this quiz and cannot attempt it again.</p>
                        <hr />
                        <div className="d-flex justify-content-end">
                            <Button onClick={() => navigate('/my-courses')} variant="outline-warning">
                                Back to Quizzes
                            </Button>
                        </div>
                    </Alert>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <Alert variant="danger">{error}</Alert>
            </Layout>
        );
    }

    if (submissionResult) {
        return (
            <Layout>
                <div className="container mt-5">
                    <Card className="text-center">
                        <Card.Header as="h3">Quiz Results</Card.Header>
                        <Card.Body>
                            <Card.Title>Your Score: {submissionResult.score}</Card.Title>
                            <Card.Text> You answered {submissionResult.correctAnswers} out of {submissionResult.totalQuestions} questions correctly. </Card.Text>
                            <Button variant="primary" onClick={() => navigate(`/quiz-results?submissionId=${submissionResult.quizSubmissionId}`)}> View Detailed Results </Button>
                        </Card.Body>
                    </Card>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6 bg-gray-100 min-h-screen">
                <div className="container mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>{quiz?.title}</h2>
                        {quiz?.timeLimit && (
                            <div className={`timer ${timer < 60 ? 'text-danger' : ''}`}>
                                <strong>Time Remaining:</strong> {formatTime(timer)}
                            </div>
                        )}
                    </div>

                    {timeUp && (
                        <Alert variant="warning" className="mb-4">
                            Time's up! Your quiz has been automatically submitted.
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        {questions.map((question, qIndex) => (
                            <Card key={question._id} className="mb-4">
                                <Card.Header>
                                    <strong>Question {qIndex + 1}:</strong> {question.questionText}
                                </Card.Header>
                                <Card.Body>
                                    {question.options.map((option, oIndex) => (
                                        <Form.Check key={oIndex} type={question.multipleCorrect ? 'checkbox' : 'radio'}
                                            id={`q${qIndex}-o${oIndex}`} name={`question-${question._id}`} label={option} value={option} checked={answers[question._id]?.includes(option) || false}
                                            onChange={(e) => handleAnswerChange(question._id, option, e.target.checked)} disabled={timeUp || submitting} className="mb-2" />
                                    ))}
                                </Card.Body>
                            </Card>
                        ))}

                        <div className="d-grid gap-2">
                            <Button variant="primary" type="submit" size="lg" disabled={submitting || timeUp} >
                                {submitting ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                        <span className="ms-2">Submitting...</span>
                                    </>
                                ) : (
                                    'Submit Quiz'
                                )}
                            </Button>
                        </div>
                    </Form>
                </div>
            </div>
        </Layout>
    );
};

export default QuizSubmission;